import * as THREE from 'three';

import {
  loadStars,
  starColor,
  starIntensity,
  starPointSize,
  type StarCatalogue,
} from '../world/stars.ts';

export interface CatalogueStarsOptions {
  /** Cap on the stars drawn, brightest first. Omit for the whole catalogue. */
  count?: number;
  /** Radius of the shell the field is drawn on, in scene units. */
  radius?: number;
  /** Multiplier on point size, for a small viewport or a crowded frame. */
  sizeScale?: number;
  /** Multiplier on intensity. */
  intensityScale?: number;
  /** Asset URL override. */
  url?: string;
}

export interface CatalogueStarsHandle {
  points: THREE.Points;
  /** Resolves with the number of stars drawn; 0 if the catalogue did not load. */
  ready: Promise<number>;
  /** Scales every star's contribution, 0 to 1; 1 unless a caller dims the field. */
  setFade(k: number): void;
  dispose(): void;
}

const starVertexShader = /* glsl */ `
  attribute vec3 aColor;
  attribute float aSize;
  uniform float uPixelRatio;
  uniform float uSizeScale;
  varying vec3 vTint;

  #include <common>
  #include <logdepthbuf_pars_vertex>

  void main() {
    vTint = aColor;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * uPixelRatio * uSizeScale;
    #include <logdepthbuf_vertex>
  }
`;

const starFragmentShader = /* glsl */ `
  precision highp float;
  varying vec3 vTint;
  uniform float uFade;

  #include <common>
  #include <logdepthbuf_pars_fragment>

  void main() {
    vec2 d = gl_PointCoord * 2.0 - 1.0;
    float r2 = dot(d, d);
    if (r2 > 1.0) discard;
    #include <logdepthbuf_fragment>
    gl_FragColor = vec4(vTint, exp(-r2 * 2.8) * uFade);
  }
`;

/**
 * The real sky: the Bright Star Catalogue as a point cloud.
 *
 * Magnitude drives point size and intensity, B-V drives colour, positions are
 * true J2000 directions on a shell of `radius`, so a view along a named star's
 * coordinates puts that star at frame centre. `src/world/stars.ts` holds the
 * photometry, `tools/atlas/bake_stars.py` the bake.
 *
 * The catalogue arrives over the network, so the geometry starts empty and fills
 * in; a failed load leaves an empty sky and warns rather than falling back to
 * noise, because noise is exactly what a broken asset would be mistaken for.
 */
export function createCatalogueStars(opts: CatalogueStarsOptions = {}): CatalogueStarsHandle {
  const radius = opts.radius ?? 2400;
  const cap = opts.count ?? Number.POSITIVE_INFINITY;
  const intensityScale = opts.intensityScale ?? 1;

  const geo = new THREE.BufferGeometry();
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  const uniforms = {
    uPixelRatio: { value: Math.min(2, dpr) },
    uSizeScale: { value: opts.sizeScale ?? 1 },
    uFade: { value: 1 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: starVertexShader,
    fragmentShader: starFragmentShader,
    // Occlusion here is draw order, not the depth test: the shell radius is
    // well inside real heliocentric distances at most framings, so a depth
    // compare against actual bodies would put the "sky" nearer than the solar
    // system it is behind. transparent:false (additive blending still applies
    // regardless - see WebGLState.setMaterial) puts this in the opaque pass,
    // which always renders before Sol's transparent disc; depthTest/depthWrite
    // false plus the low renderOrder below keep it from winning against, or
    // leaving depth for, anything drawn after it.
    transparent: false,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geo, mat);
  points.name = 'catalogue-stars';
  // The field IS the sky, and its geometry is empty until the load lands, so a
  // bounding sphere would either not exist yet or cull the whole shell.
  points.frustumCulled = false;
  // Lower than anything else drawn in the opaque pass, so every body and Sol's
  // glow draw over the field regardless of where the shell radius puts it.
  points.renderOrder = -100000;

  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    points.removeFromParent();
    geo.dispose();
    mat.dispose();
  };
  points.userData['dispose'] = dispose;

  const fill = (cat: StarCatalogue): number => {
    const n = Math.min(cat.count, cap);
    const pos = new Float32Array(n * 3);
    const col = new Float32Array(n * 3);
    const size = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      pos[i * 3] = (cat.dir[i * 3] ?? 0) * radius;
      pos[i * 3 + 1] = (cat.dir[i * 3 + 1] ?? 0) * radius;
      pos[i * 3 + 2] = (cat.dir[i * 3 + 2] ?? 0) * radius;
      const mag = cat.mag[i] ?? 6.5;
      const rgb = starColor(cat.bv[i] ?? Number.NaN);
      const k = starIntensity(mag) * intensityScale;
      col[i * 3] = rgb[0] * k;
      col[i * 3 + 1] = rgb[1] * k;
      col[i * 3 + 2] = rgb[2] * k;
      size[i] = starPointSize(mag);
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
    geo.setAttribute('aSize', new THREE.BufferAttribute(size, 1));
    return n;
  };

  const ready = loadStars(opts.url)
    .then((cat) => (disposed ? 0 : fill(cat)))
    .catch((err: unknown) => {
      console.warn('[sky] star catalogue did not load, the sky is empty:', err);
      return 0;
    });

  const setFade = (k: number): void => { uniforms.uFade.value = k; };

  return { points, ready, setFade, dispose };
}

/** Convenience wrapper over `createCatalogueStars`; callers add `.points` and keep the handle to dispose it. */
export function createStarfield(count = 9000, radius = 2400): CatalogueStarsHandle {
  return createCatalogueStars({ count, radius });
}
